import { useState, useEffect, useCallback, useRef } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useAuth } from '../context/AuthContext'
import { getRequests, reorderRequests, updatePriority, getCustomers, generateViewerToken } from '../api/client'
import CollapsibleStatusGroup from './CollapsibleStatusGroup'
import RequestRow from './RequestRow'
import CustomerList from './CustomerList'
import xanoLogo from '../assets/xano-logo.png'

const STATUS_GROUPS = [
  { id: 'idea', label: 'Idea' },
  { id: 'in-progress for future release', label: 'In-progress for Future Release' },
  { id: 'ready for release', label: 'Ready for Release' },
  { id: 'released', label: 'Released' },
]

function Board() {
  const { user, logout } = useAuth()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeId, setActiveId] = useState(null)
  const [customers, setCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef(null)

  const isAdmin = user?.role === 'admin'
  const isViewer = user?.role === 'viewer'
  const canEdit = user?.role === 'editor' || isAdmin

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const fetchRequests = useCallback(async (customerId = null, viewerMode = false) => {
    try {
      setLoading(true)
      const data = await getRequests(customerId, viewerMode)
      setRequests(Array.isArray(data) ? data : [])
      setError('')
    } catch (err) {
      console.error('Failed to fetch requests:', err)
      setError('Failed to load requests')
    } finally {
      setLoading(false)
    }
  }, [])

  // Context-aware refetch that preserves current view (admin+customer, viewer, editor)
  const refetchRequests = useCallback(() => {
    if (isAdmin && selectedCustomer) {
      fetchRequests(selectedCustomer.id, false)
    } else if (isViewer) {
      fetchRequests(null, true)
    } else {
      fetchRequests(null, false)
    }
  }, [fetchRequests, isAdmin, isViewer, selectedCustomer])

  // Fetch requests when customer selection changes (for admins) or on mount (for non-admins)
  useEffect(() => {
    if (isAdmin) {
      // Admin: only fetch requests when a customer is selected
      if (selectedCustomer) {
        fetchRequests(selectedCustomer.id, false)
      } else {
        // Clear requests when switching back to "All Customers"
        setRequests([])
      }
    } else if (isViewer) {
      // Viewer: use viewer-specific endpoint
      fetchRequests(null, true)
    } else {
      // Editor: fetch requests on mount
      fetchRequests(null, false)
    }
  }, [fetchRequests, isAdmin, isViewer, selectedCustomer])

  // Fetch customers for admin dropdown
  useEffect(() => {
    if (isAdmin) {
      setCustomersLoading(true)
      getCustomers()
        .then((data) => {
          const customerList = Array.isArray(data) ? data : []
          setCustomers(customerList)

          // Check for customer query param and set selected customer
          const params = new URLSearchParams(window.location.search)
          const customerId = params.get('customer')
          if (customerId) {
            const customer = customerList.find(c => c.id === customerId)
            if (customer) {
              setSelectedCustomer(customer)
            }
          }
        })
        .catch((err) => console.error('Failed to fetch customers:', err))
        .finally(() => setCustomersLoading(false))
    }
  }, [isAdmin])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Priority sort order (highest priority first)
  const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 }

  const sortByPriorityThenTitle = (a, b) => {
    const priorityA = PRIORITY_ORDER[a.customer_priority] ?? 4
    const priorityB = PRIORITY_ORDER[b.customer_priority] ?? 4
    if (priorityA !== priorityB) return priorityA - priorityB
    return (a.title || '').localeCompare(b.title || '')
  }

  const getRequestsByStatus = (status) => {
    return requests
      .filter((r) => r.customer_status?.toLowerCase() === status)
      .sort(sortByPriorityThenTitle)
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveId(null)

    if (!over || !canEdit) return

    const activeRequest = requests.find((r) => r.link_id === active.id)
    if (!activeRequest) return

    const overRequest = requests.find((r) => r.link_id === over.id)
    if (!overRequest || active.id === over.id) return

    // Only allow reordering within same status column
    if (activeRequest.customer_status?.toLowerCase() !== overRequest.customer_status?.toLowerCase()) return

    // Get requests in the same status column
    const columnRequests = getRequestsByStatus(activeRequest.customer_status)
    const oldIndex = columnRequests.findIndex((r) => r.link_id === active.id)
    const newIndex = columnRequests.findIndex((r) => r.link_id === over.id)

    if (oldIndex === newIndex) return

    // Reorder locally first (optimistic update)
    const reordered = [...columnRequests]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)

    // Assign new stack ranks
    const updates = reordered.map((item, index) => ({
      link_id: item.link_id,
      stack_rank: index + 1,
    }))

    // Update local state optimistically
    setRequests((prev) => {
      const otherRequests = prev.filter(
        (r) => r.customer_status !== activeRequest.customer_status
      )
      const updatedColumn = reordered.map((item, index) => ({
        ...item,
        stack_rank: index + 1,
      }))
      return [...otherRequests, ...updatedColumn]
    })

    // Call API
    try {
      await reorderRequests(updates)
    } catch (err) {
      console.error('Failed to reorder:', err)
      refetchRequests()
    }
  }

  const handlePriorityChange = async (linkId, newPriority) => {
    // Optimistic update
    setRequests((prev) =>
      prev.map((r) =>
        r.link_id === linkId ? { ...r, customer_priority: newPriority } : r
      )
    )

    try {
      await updatePriority(linkId, newPriority)
    } catch (err) {
      console.error('Failed to update priority:', err)
      refetchRequests()
    }
  }

  const activeRequest = activeId
    ? requests.find((r) => r.link_id === activeId)
    : null

  // Filter customers based on search query
  const filteredCustomers = searchQuery
    ? customers.filter((customer) =>
        customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : customers

  // Filter requests based on search query
  const filteredRequests = searchQuery
    ? requests.filter((request) =>
        request.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : requests

  // Get filtered requests by status
  const getFilteredRequestsByStatus = (status) => {
    return filteredRequests
      .filter((r) => r.customer_status?.toLowerCase() === status)
      .sort(sortByPriorityThenTitle)
  }

  // Clear search when switching views and update URL
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
    setSearchQuery('')

    // Update URL query parameter
    const url = new URL(window.location)
    if (customer) {
      url.searchParams.set('customer', customer.id)
    } else {
      url.searchParams.delete('customer')
    }
    window.history.pushState({}, '', url)
  }

  // Generate viewer token for a customer and refresh the list
  const handleGenerateToken = async (customerId) => {
    try {
      await generateViewerToken(customerId)
      // Refresh customer list to get the new token
      const data = await getCustomers()
      setCustomers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to generate viewer token:', err)
      setError('Failed to generate viewer link')
    }
  }

  return (
    <div className="min-h-screen bg-xano-bg">
      {/* Header */}
      <header className="bg-xano-card border-b border-xano-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Xano Logo */}
            <img
              src={xanoLogo}
              alt="Xano"
              className="w-10 h-10 rounded-lg"
            />

            {/* Customer name / switcher */}
            {isAdmin ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                  className="flex items-center gap-2 hover:bg-xano-cardHover px-2 py-2 rounded-md transition"
                >
                  <div className="text-left">
                    <p className="text-base font-semibold text-xano-text">
                      {selectedCustomer?.name || 'All Customers'}
                    </p>
                    <p className="text-xs text-xano-textMuted">Requests Portal</p>
                  </div>
                  <svg
                    className={`w-4 h-4 text-xano-textMuted transition-transform ${showCustomerDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Customer Dropdown */}
                {showCustomerDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-xano-card border border-xano-border rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <button
                        onClick={() => {
                          handleSelectCustomer(null)
                          setShowCustomerDropdown(false)
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-xano-cardHover transition ${
                          !selectedCustomer ? 'text-xano-accent' : 'text-xano-text'
                        }`}
                      >
                        All Customers
                      </button>
                      <div className="border-t border-xano-border my-1"></div>
                      {customers.map((customer) => (
                        <button
                          key={customer.id}
                          onClick={() => {
                            handleSelectCustomer(customer)
                            setShowCustomerDropdown(false)
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-xano-cardHover transition ${
                            selectedCustomer?.id === customer.id ? 'text-xano-accent' : 'text-xano-text'
                          }`}
                        >
                          {customer.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-left">
                <p className="text-base font-semibold text-xano-text">
                  {user?.customer_name || 'Requests'}
                </p>
                <p className="text-xs text-xano-textMuted">Xano Requests Portal</p>
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="flex-1 max-w-md mx-8">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-xano-textMuted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isAdmin && !selectedCustomer ? "Search customers..." : "Search requests..."}
                className="w-full pl-10 pr-4 py-2 bg-xano-bg border border-xano-border rounded-lg text-sm text-xano-text placeholder-xano-textMuted focus:outline-none focus:border-xano-accent transition"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xano-textMuted hover:text-xano-text"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-xano-text">{user?.name}</p>
              <p className="text-xs text-xano-textMuted capitalize">{user?.role}</p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 text-sm font-medium text-xano-textMuted hover:text-xano-text bg-xano-card hover:bg-xano-cardHover border border-xano-border rounded-md transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 pt-8 pb-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-xano-textMuted">Loading...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error}</div>
          </div>
        ) : isAdmin && !selectedCustomer ? (
          // Admin view with no customer selected - show customer list
          customersLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xano-textMuted">Loading customers...</div>
            </div>
          ) : (
            <CustomerList
              customers={filteredCustomers}
              onSelectCustomer={handleSelectCustomer}
              onGenerateToken={handleGenerateToken}
            />
          )
        ) : (
          // Customer selected or non-admin - show requests
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Collapsible Status Groups */}
            <div className="space-y-8">
              {STATUS_GROUPS.map((group) => (
                <CollapsibleStatusGroup
                  key={group.id}
                  id={group.id}
                  title={group.label}
                  requests={getFilteredRequestsByStatus(group.id)}
                  userRole={user?.role}
                  canEdit={canEdit}
                  onPriorityChange={handlePriorityChange}
                />
              ))}
            </div>

            <DragOverlay>
              {activeRequest ? (
                <RequestRow
                  request={activeRequest}
                  userRole={user?.role}
                  isDragging
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </main>
    </div>
  )
}

export default Board
